'use client';

/**
 * Wrapper client del Theme Editor. Sceglie l'engine in base a `engine`:
 *  - 'live'   → <ThemeEditor> dal submodule live-theme-editor (craft.js), caricato
 *               via next/dynamic ssr:false (client-only) e collegato allo storage
 *               backend (POST /api/theme-editor/save).
 *  - 'legacy' → editor inline storico (self-save interno).
 */

import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import ThemeEditorLegacy from './legacy/ThemeEditorLegacy';
import type { ThemeEngine } from '@/lib/theme-editor-access';
import { usePageContentStore } from '@/components/visual-editing/PageContentProvider';
import { getRegistryField } from '@/lib/visual-editing/content-registry';
import { useLocale } from '@/components/LocaleProvider';

type Overrides = Record<string, Record<string, unknown>>;

// Engine "live": editor pick-and-edit dal submodule, client-only → ssr:false.
const LiveThemeEditor = dynamic(
  () => import('@/vendor/live-theme-editor/src').then((m) => m.ThemeEditor),
  { ssr: false },
);

export default function ThemeEditorClient({
  engine,
  initialOverrides,
}: {
  engine: ThemeEngine;
  initialOverrides: Overrides;
}) {
  const store = usePageContentStore();
  const { locale } = useLocale();
  const loc = locale || 'it';

  // API contenuti editabili per l'editor: risolve data-cid → registry + valore
  // corrente, salva via /api/page-content/manage e applica optimistic (live).
  const content = useMemo(
    () => ({
      resolve: (cid: string) => {
        const f = getRegistryField(cid);
        if (!f) return null;
        const bucket = f.i18n
          ? store.content[loc] || store.content.it || {}
          : store.content.shared || {};
        return {
          type: (f.type === 'image' ? 'image' : 'text') as 'text' | 'image',
          label: f.label,
          value: bucket[f.path] ?? '',
        };
      },
      save: async (cid: string, value: string) => {
        const f = getRegistryField(cid);
        if (!f) throw new Error('campo non in registry');
        const res = await fetch('/api/page-content/manage', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: f.path, value, locale: f.i18n ? loc : undefined }),
        });
        if (!res.ok) throw new Error('save failed');
        store.applyPatch(f.path, value, f.i18n ? loc : null);
      },
    }),
    [store, loc],
  );

  if (engine === 'legacy') {
    return <ThemeEditorLegacy initialOverrides={initialOverrides as never} />;
  }

  return (
    <LiveThemeEditor
      initialOverrides={initialOverrides as never}
      content={content}
      onSave={async (overrides: unknown, ops?: unknown) => {
        const res = await fetch('/api/theme-editor/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ overrides, ops }),
        });
        if (!res.ok) {
          // Espone la causa reale (status + error body) invece del generico "save failed".
          let detail = '';
          try {
            const data = await res.json();
            detail = data?.error ? `: ${data.error}` : '';
          } catch {
            detail = `: ${(await res.text().catch(() => '')).slice(0, 200)}`;
          }
          throw new Error(`save failed [${res.status}]${detail}`);
        }
      }}
      onAi={async (reqBody: unknown) => {
        const res = await fetch('/api/theme-editor/ai', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(reqBody),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'ai failed');
        return { overrides: data.overrides || {}, note: data.note };
      }}
    />
  );
}
